/**
 * shellLogger — lightweight structured logger for the shell/wizard observability layer.
 *
 * Design goals:
 *   - Structured events with a consistent field set:
 *       event, flowId, shopId, route, status, durationMs, errorCode, ts (unix ms).
 *   - Deep redaction of sensitive keys before any output:
 *       token, secret, sig, hmac, auth, password, accesstoken,
 *       access_token, apikey, api_key.
 *   - debug-level events suppressed in production unless
 *     window.__KEA_LOG_LEVEL === 'debug'.
 *   - warn / error always emitted to console regardless of environment.
 *   - In all environments: events appended to window.__KEA_LOGS array so that
 *     E2E tests and devtools can inspect the full event sequence.
 *
 * Usage:
 *   import { shellLogger, generateFlowId } from './shellLogger';
 *   const flowId = generateFlowId();
 *   shellLogger.info('bootstrap.start', { flowId, shopId: '42' });
 *   shellLogger.warn('wizard.complete.error', { flowId, errorCode: 'TIMEOUT' });
 */

// ---------------------------------------------------------------------------
// Sensitive-key redaction
// ---------------------------------------------------------------------------

/**
 * Keys whose values must never appear in log output.
 * Matching is case-insensitive (keys are lowercased before comparison).
 */
const SENSITIVE_KEYS = new Set([
  'token',
  'secret',
  'sig',
  'hmac',
  'auth',
  'password',
  'accesstoken',
  'access_token',
  'apikey',
  'api_key',
]);

/**
 * Deep-clone an object, replacing any value whose key matches SENSITIVE_KEYS
 * (case-insensitive) with the string `'[REDACTED]'`.
 *
 * Arrays and primitives are returned unchanged.
 *
 * @param {*} value
 * @returns {*}
 */
export function redactFields(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;

  const result = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [k, v] of Object.entries(value)) {
    if (SENSITIVE_KEYS.has(k.toLowerCase())) {
      result[k] = '[REDACTED]';
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      result[k] = redactFields(v);
    } else {
      result[k] = v;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Flow ID generation
// ---------------------------------------------------------------------------

/**
 * Generate a unique identifier for one bootstrap / wizard-completion run.
 * Format: `fl-<timestamp_base36>-<random_6chars>` — short, URL-safe, sortable.
 *
 * @returns {string}
 */
export function generateFlowId() {
  return `fl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// Level gate
// ---------------------------------------------------------------------------

function isDebugEnabled() {
  if (typeof window !== 'undefined' && window.__KEA_LOG_LEVEL === 'debug') return true;
  return process.env.NODE_ENV !== 'production';
}

// ---------------------------------------------------------------------------
// Core emitter
// ---------------------------------------------------------------------------

function emit(level, event, fields) {
  if (level === 'debug' && !isDebugEnabled()) return;

  const entry = {
    ts: Date.now(),
    level,
    event,
    ...redactFields(fields || {}),
  };

  // Append to global buffer for E2E / devtools inspection.
  if (typeof window !== 'undefined') {
    if (!Array.isArray(window.__KEA_LOGS)) window.__KEA_LOGS = [];
    window.__KEA_LOGS.push(entry);
  }

  /* eslint-disable no-console */
  if (process.env.NODE_ENV !== 'production') {
    // Dev / test: emit every level.
    (console[level] || console.log).call(console, '[kea]', event, entry);
  } else if (level === 'warn' || level === 'error') {
    // Production: emit only warn and error.
    console[level].call(console, '[kea]', event, entry);
  }
  /* eslint-enable no-console */
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const shellLogger = {
  /** Diagnostic detail — suppressed in production unless __KEA_LOG_LEVEL='debug'. */
  debug: (event, fields) => emit('debug', event, fields),
  /** Informational — emitted in dev/test, suppressed in production. */
  info: (event, fields) => emit('info', event, fields),
  /** Warning — always emitted. */
  warn: (event, fields) => emit('warn', event, fields),
  /** Error — always emitted. */
  error: (event, fields) => emit('error', event, fields),
};
